import { useState, type SubmitEvent } from 'react'
import { CheckCircle2Icon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import type { AccommodationDetail, LocationPriceReportRequest } from '@/api/accommodation/types'
import { useCreateLocationPriceReportMutation } from '@/api/accommodation/hooks'
import { loadStoredEmail, saveStoredEmail } from '@/lib/userEmail'

interface PriceGuaranteeFormProps {
  accommodation: AccommodationDetail
  onSent: (report: LocationPriceReportRequest) => void
  onClose: () => void
}

interface FormValues {
  url: string
  pricePerNight: string
  email: string
  gdpr: boolean
}

type FieldErrors = Partial<Record<keyof FormValues, string>>

const initialValues: FormValues = {
  url: '',
  pricePerNight: '',
  email: '',
  gdpr: false,
}

function validate(values: FormValues): FieldErrors {
  const errors: FieldErrors = {}

  if (!values.url.trim()) {
    errors.url = 'Zadejte odkaz na výhodnější nabídku.'
  } else if (!values.url.includes('.')) {
    errors.url = 'Zadejte platný odkaz.'
  }
  const price = Number(values.pricePerNight.replace(/\s/g, ''))
  if (!values.pricePerNight.trim() || !Number.isFinite(price) || price <= 0) {
    errors.pricePerNight = 'Zadejte nalezenou cenu za noc.'
  }
  if (!/^\S+@\S+\.\S+$/.test(values.email.trim())) {
    errors.email = 'Zadejte platný e-mail.'
  }
  if (!values.gdpr) {
    errors.gdpr = 'Pro odeslání je nutný souhlas se zpracováním údajů.'
  }

  return errors
}

export function PriceGuaranteeForm({ accommodation, onSent, onClose }: PriceGuaranteeFormProps) {
  const [values, setValues] = useState<FormValues>(() => ({
    ...initialValues,
    email: loadStoredEmail(),
  }))
  const [errors, setErrors] = useState<FieldErrors>({})
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isSent, setIsSent] = useState(false)

  const mutation = useCreateLocationPriceReportMutation(accommodation.slug)

  const updateField = <K extends keyof FormValues>(key: K, value: FormValues[K]) => {
    setValues((prev) => ({ ...prev, [key]: value }))
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev }
        delete next[key]
        return next
      })
    }
  }

  const handleSubmit = (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault()
    setSubmitError(null)
    const validation = validate(values)
    if (Object.keys(validation).length > 0) {
      setErrors(validation)
      return
    }

    saveStoredEmail(values.email)
    const report: LocationPriceReportRequest = {
      url: values.url.trim(),
      pricePerNight: Number(values.pricePerNight.replace(/\s/g, '')),
      email: values.email.trim(),
    }

    mutation.mutate(report, {
      onSuccess: () => {
        setIsSent(true)
        onSent(report)
      },
      onError: () => {
        setSubmitError('Žádost se nepodařilo odeslat. Zkuste to prosím znovu, případně nás kontaktujte přímo.')
      },
    })
  }

  if (isSent) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-10 text-center">
        <div className="space-y-1">
          <div className="flex justify-center rounded-full bg-green-50 p-1 text-green-600">
            <CheckCircle2Icon className="size-10" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900">Děkujeme, nabídku prověříme.</h3>
          <p className="max-w-sm text-sm text-slate-600">
            Pokud to půjde, cenu dorovnáme. Ozveme se vám na e-mail{' '}
            <span className="font-medium text-slate-900">{values.email.trim()}</span>.
          </p>
        </div>
        <Button type="button" variant="outline" onClick={onClose}>
          Zavřít
        </Button>
      </div>
    )
  }

  return (
    <form noValidate onSubmit={handleSubmit} className="space-y-5">
      <FieldWrapper id="price-guarantee-url" label="Kde jste našli lepší cenu?" error={errors.url}>
        <Input
          id="price-guarantee-url"
          name="url"
          type="url"
          inputMode="url"
          placeholder="https://…"
          value={values.url}
          aria-invalid={!!errors.url}
          onChange={(e) => updateField('url', e.target.value)}
        />
      </FieldWrapper>

      <FieldWrapper id="price-guarantee-price" label="Nalezená cena za noc (Kč)" error={errors.pricePerNight}>
        <Input
          id="price-guarantee-price"
          name="pricePerNight"
          inputMode="numeric"
          placeholder="např. 2 500"
          value={values.pricePerNight}
          aria-invalid={!!errors.pricePerNight}
          onChange={(e) => updateField('pricePerNight', e.target.value)}
        />
      </FieldWrapper>

      <FieldWrapper id="price-guarantee-email" label="Váš e-mail" error={errors.email}>
        <Input
          id="price-guarantee-email"
          name="email"
          type="email"
          autoComplete="email"
          value={values.email}
          aria-invalid={!!errors.email}
          onChange={(e) => updateField('email', e.target.value)}
        />
      </FieldWrapper>

      <div className="space-y-1">
        <label htmlFor="price-guarantee-gdpr" className="flex items-start gap-2 text-sm text-slate-700">
          <Checkbox
            id="price-guarantee-gdpr"
            checked={values.gdpr}
            aria-invalid={!!errors.gdpr}
            onCheckedChange={(checked) => updateField('gdpr', checked === true)}
            className="mt-0.5"
          />
          <span>Souhlasím se zpracováním osobních údajů pro účely posouzení žádosti.</span>
        </label>
        {errors.gdpr && <p className="pl-6 text-sm text-red-600">{errors.gdpr}</p>}
      </div>

      {submitError && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{submitError}</p>}

      <Button type="submit" variant="red" className="w-full" disabled={mutation.isPending}>
        {mutation.isPending ? 'Odesílám…' : 'Odeslat žádost'}
      </Button>
    </form>
  )
}

interface FieldWrapperProps {
  id: string
  label: string
  error?: string
  children: React.ReactNode
}

function FieldWrapper({ id, label, error, children }: FieldWrapperProps) {
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  )
}
